import { useEffect, useRef, useState } from 'react'
import BackwardsHero from '../../components/backwards/BackwardsHero'
import CultureCallout from '../../components/backwards/CultureCallout'
import FaqAccordion from '../../components/backwards/FaqAccordion'
import LearningObjectivesCard from '../../components/backwards/LearningObjectivesCard'
import MethodBlock from '../../components/backwards/MethodBlock'
import PlaybookTopBar from '../../components/backwards/PlaybookTopBar'
import PressReleaseCard from '../../components/backwards/PressReleaseCard'
import ProblemCards from '../../components/backwards/ProblemCards'
import ProductAccordion from '../../components/backwards/ProductAccordion'
import SectionFooterNav from '../../components/backwards/SectionFooterNav'
import SectionHeader from '../../components/backwards/SectionHeader'
import SectionIndex from '../../components/backwards/SectionIndex'
import SegmentedTabs from '../../components/backwards/SegmentedTabs'
import ServicesComparisonTable from '../../components/backwards/ServicesComparisonTable'
import SupportMaterialCard from '../../components/backwards/SupportMaterialCard'
import ValuePropStats from '../../components/backwards/ValuePropStats'
import { cn } from '../../utils/cn'

const PLACEHOLDER = '— conteúdo a definir —'

const learningObjectives = [
  'Qual é o posicionamento da Nex2AI',
  'O que significa AI-First',
  'Como a Nex2AI se diferencia no mercado',
  'Por que não vendemos apenas tecnologia',
  'Como explicar a proposta de valor da empresa',
]

const indexItems = [
  {
    number: '01',
    title: 'Como você vai aprender',
    description:
      'Através do método Working Backwards, começamos pelo resultado que o cliente precisa, não pela tecnologia que queremos vender.',
  },
  {
    number: '02',
    title: 'Conhecendo a Nex2AI',
    description:
      'Introdução à visão da empresa, cultura AI-First, posicionamento de mercado e tese estratégica.',
  },
  {
    number: '03',
    title: 'Nossos Serviços',
    description:
      'Explore os serviços e compreenda como cada solução é posicionada, apresentada e aplicada dentro do contexto da necessidade do cliente.',
  },
]

const methodBody =
  'O método **Working Backwards** foi escolhido como estrutura de aprendizado dos serviços da Nex2AI porque facilita a compreensão da narrativa comercial antes dos detalhes operacionais da solução.\n\nÉ um framework criado pela Amazon onde você começa imaginando o resultado final (como se estivesse anunciando o serviço para o mercado) e trabalha de trás para frente. Ao invés de começar pela tecnologia, você começa pelo problema que resolve e o valor que entrega.\n\nIsso ajuda o time comercial a desenvolver uma comunicação mais clara, consultiva e orientada a valor, padronizando a forma como a empresa comunica ao mercado.\n\nO Objetivo é acelerar o entendimento comercial. Não aprofundar tecnicamente.'

const methodBullets = [
  'Qual problema o serviço resolve de fato',
  'Como apresentar de forma simples, estratégica e executiva',
  'Como contextualizar a solução comercialmente',
  'Para quem ele faz sentido e por que',
  'Quais dúvidas vão surgir e como respondê-las com segurança',
]

const pressReleaseIntro = [
  'O **Press Release** é uma forma estruturada de apresentar um serviço pela ótica do impacto e da transformação que ele gera para o cliente, em um formato de notícia, como se estivéssemos comunicando o lançamento de um novo produto em veículos de comunicação, por exemplo.',
  'O objetivo não é decorar textos, mas entender como o serviço deve ser apresentado comercialmente.',
]

// Card de exemplo: parágrafo, citação (no meio), parágrafo, parágrafo.
const pressReleaseBlocks = [
  {
    type: 'p',
    text: 'Empresas de médio e grande porte agora podem sair do ciclo de pilotos isolados e ganhar clareza sobre quais iniciativas de inteligência artificial realmente merecem investimento — e em qual ordem devem ser executadas para gerar impacto operacional.',
  },
  {
    type: 'quote',
    text: '"A maioria das empresas já percebeu que precisa adotar IA, mas ainda enfrenta dificuldade para transformar iniciativas isoladas em uma estratégia executável e sustentável."',
    author: 'Head Comercial, Nex2AI',
  },
  {
    type: 'p',
    text: 'Ao longo da jornada, a Nex2AI avalia dimensões como governança, operação, cultura, maturidade de dados, oportunidades de automação e capacidade de execução, permitindo que a empresa visualize com maior clareza onde estão os principais ganhos potenciais.',
  },
  {
    type: 'p',
    text: 'Ao final do processo, a organização recebe um diagnóstico estruturado acompanhado de um roadmap priorizado de iniciativas, facilitando tomada de decisão, alinhamento executivo e construção de uma estratégia AI-First mais consistente...',
  },
]

const faqIntro =
  'Durante o estudo dos nossos serviços, você encontrará uma seção de FAQ que foi criado para apoiar reuniões comerciais, apresentações executivas e conversas estratégicas. Se trata de um mapeamento prévio (simulado) de inúmeras dúvidas e questionamentos comuns que são trazidos pelos clientes sobre os nossos produtos e serviços.'

const faqCallout =
  'Ler a seção de **FAQ** de cada um dos serviços, vai te ajudar a ganhar confiança sem precisar aprofundar tecnicamente no produto ou serviço disponíveis na Nex2AI.'

const faqItems = [
  {
    question: 'O que é exatamente esse serviço?',
    answer:
      'É uma estrutura de diagnóstico estratégico criada para ajudar empresas a entenderem seu nível atual de maturidade em inteligência artificial, identificarem oportunidades prioritárias e construírem um plano de execução AI-First com maior clareza e direcionamento.',
  },
  { question: 'Isso serve para minha empresa?', answer: PLACEHOLDER },
  { question: 'Quanto tempo leva?', answer: PLACEHOLDER },
  { question: 'Qual resultado eu vou ter?', answer: PLACEHOLDER },
]

const problemCards = [
  {
    tag: 'Problema 1',
    tagColor: '#CA3500',
    title: 'As empresas não sabem por onde começar.',
    text: 'Têm pressão do board para "fazer algo com IA", têm acesso ao Copilot, ao Azure AI Foundry, ao ChatGPT, mas não têm critério para decidir quais casos de uso atacar primeiro, com qual ROI esperado, com qual risco regulatório.',
    solution: {
      product: 'Radar de IA',
      text: 'Executar o Diagnóstico de Maturidade + Roadmap de Casos de Uso ( Priorizados por ROI )',
    },
  },
  {
    tag: 'Problema 2',
    tagColor: '#CA3500',
    title: 'Quando começam, entregam mal.',
    text: 'Os times de desenvolvimento usam IA de forma fragmentada: cada dev faz de um jeito, sem processo, sem métricas, sem guardrails. IA com caos é só caos mais rápido.',
    solution: {
      product: 'AI-Driven Development',
      text: 'Redesenho do processo de desenvolvimento com IA em cada etapa, do upstream ao merge. Times entregam 2-3x mais com a mesma headcount.',
    },
  },
  {
    tag: 'Problema 3',
    tagColor: '#CA3500',
    title: 'Quando escalam, viram alvo.',
    text: 'Modelos em produção sem rastreabilidade, sem gestão de viés, sem conformidade com LGPD e normas setoriais, e o regulador, o board e o cliente final estão cada vez mais atentos a isso.',
    solution: {
      product: 'Governança de IA',
      text: 'Framework ISO 42001 + plataforma de controle + conformidade LGPD. Governança como habilitador de negócio, não como freio.',
    },
  },
]

const problemLead =
  'A maioria das empresas brasileiras chegou a 2026 com três problemas simultâneos de IA, e nenhum deles é falta de ferramenta:'

const problemSummary =
  'Os três produtos da Nex2AI atacam exatamente esses três momentos: o Radar de IA responde "por onde começar", o AI-Driven Development resolve como entregar com qualidade", e a Governança de IA garante "como escalar com segurança"..'

const comparisonLead =
  'Pense nos três produtos como uma jornada, não como opções excludentes. Eles são sequenciais por design, mas podem ser contratados de forma independente e dependendo apenas do momento do cliente.'

const comparisonColumns = [
  'Produto',
  'Pergunta que responde',
  'O que entrega na prática',
  'Para quem',
]

const comparisonRows = [
  [
    'Radar de IA',
    'Por onde começo?',
    'Diagnóstico de maturidade + roadmap de casos de uso priorizados por ROI + execução opcional dos casos priorizados',
    'CEO, COO, CIO, VP de Inovação, Head de TI',
  ],
  [
    'AI-Driven Development',
    'Como entrego com qualidade?',
    'Redesenho do processo de desenvolvimento com IA em cada etapa — do upstream ao merge. Times entregam 2-3x mais com a mesma headcount.',
    'CTO, CIO, Tech Lead, VP de Engenharia',
  ],
  [
    'Governança de IA',
    'Como escalo com segurança?',
    'Framework ISO 42001 + plataforma de controle + conformidade LGPD. Governança como habilitador de negócio, não como freio.',
    'CIO, CISO, CDO, Risk & Compliance, setores regulados',
  ],
]

const valuePropIntro =
  '"A sua empresa provavelmente já tem acesso a ferramentas de IA. O problema é que ninguém usa de forma organizada, cada um faz de um jeito, e você não tem como medir se está gerando resultado — nem como mostrar pro board ou pro regulador que está fazendo certo.\n\nA Nex2AI entra e resolve isso em três frentes: te ajuda a saber por onde começar, faz o time de desenvolvimento entregar mais com menos retrabalho, e garante que tudo isso aconteça dentro dos limites de segurança e compliance que a sua empresa precisa."'

const valuePropNoteTitle = 'A Nex2AI não vende apenas tecnologia'

const valuePropNote =
  'Qualquer um pode vender acesso ao Claude ou ao GPT. O valor não está no modelo de IA — está no processo que define como o modelo é usado, com quais dados, com quais guardrails, gerando quais métricas.\n\nÉ por isso que a Nex2AI cobra pelo método, não pela ferramenta. A ferramenta o cliente muitas vezes já tem ou pode comprar diretamente.'

const valuePropStatsLabel = 'O McKinsey chama isso de "Rewiring":'

const valuePropClosing =
  'A Nex2AI opera exatamente nesse 70%. É onde o ROI mora.'

const valuePropStats = [
  { value: '10%', label: 'Algoritmo', highlight: false },
  { value: '20%', label: 'Tecnologia', highlight: false },
  { value: '70%', label: 'Pessoas e Processo', highlight: true },
]

const productTabsConfig = [
  { id: 'pressRelease', label: 'Press Release' },
  { id: 'faq', label: 'FAQ' },
  { id: 'contratacao', label: 'Contratação' },
  { id: 'paraQuem', label: 'Para Quem' },
]

const placeholderContent = { headline: PLACEHOLDER, body: [] }

// Aba "Contratação" — idêntica nos três produtos (template comum no Figma).
const contratacaoContent = {
  columns: [
    {
      title: 'Modelo de Contratação',
      items: [
        'Projeto fixo com escopo definido (30 dias)',
        'Pagamento em 2 parcelas: 50% na assinatura, 50% na entrega',
        'Sem recorrência obrigatória',
        'Inclui NDA e sigilo total das informações',
        'Entregáveis documentados e de propriedade do cliente',
      ],
    },
    {
      title: 'Faixa de Investimento',
      items: [
        'PMEs (até 500 colaboradores): faixa entry',
        'Midmarket (500–5.000): faixa standard',
        'Enterprise (5.000+): faixa premium com customizações',
        'Desconto para contratação combinada com AI Squad',
      ],
    },
  ],
}

const radarFaq = [
  {
    question:
      'O Radar é só um diagnóstico? Ou a Nex2AI também executa os casos de uso priorizados?',
    answer:
      'Os dois. O Radar entrega o mapa, quais casos de uso atacar, em qual ordem, com qual ROI esperado e quais riscos. Mas a Nex2AI pode, e muitas vezes fica, como responsável pela execução dos casos priorizados. Na Unifique, por exemplo, o resultado do diagnóstico virou três casos de uso em execução simultânea: C01 (scoring de horas extras e fraude operacional), C02 (detecção de anomalias orçamentárias em 50 mil documentos/mês) e C03 (modelo de propensão de compra com automação de jornada). O time da Nex2AI, engenheiros de IA, ML engineers, data scientists — opera como squad gerenciada dentro do cliente. Diagnóstico e execução não são serviços separados; são a mesma jornada.',
  },
  {
    question: 'Quanto tempo dura e o que é entregue, exatamente?',
    answer:
      '**Duração média: 4 a 6 semanas.**\n\n1. **Mapa de Maturidade AI-First**\nAvaliação da empresa nos eixos de dados, infraestrutura, cultura, talentos e processos.\n\n2. **Inventário de Casos de Uso Priorizados**\nIdentificação e priorização de oportunidades com base em impacto, viabilidade, esforço e prontidão dos dados.\n\n3. **Análise de Risco Regulatório**\nClassificação dos casos de uso em níveis de risco (baixo, médio, alto ou crítico), considerando aspectos de governança e conformidade.\n\n4. **Roadmap de Evolução**\nPlano estruturado com marcos trimestrais, responsáveis e estimativas de investimento para implementação das iniciativas.\n\n5. **Diagnóstico de Governança**\nIdentificação de gaps existentes e mapeamento de aderência às diretrizes da ISO 42001.\n\n6. **Plano de Execução**\nProposta de implementação para os casos de uso priorizados, quando aplicável, conectando estratégia e execução.',
  },
  {
    question:
      'Como vocês priorizam os casos de uso? No final, não acaba sendo definido apenas pelo que o C-level quer?',
    answer:
      "Esse é exatamente um dos principais problemas que o **Radar de IA** busca evitar.\nO mercado costuma chamar esse comportamento de **HiPPO Bias** (Highest Paid Person's Opinion): quando a priorização é influenciada principalmente pela opinião da pessoa com maior cargo ou remuneração, e não por critérios objetivos.\n\nPara reduzir esse risco, utilizamos uma matriz de priorização baseada em quatro fatores: impacto no negócio, viabilidade técnica, esforço de implementação e prontidão dos dados. Dessa forma, cada oportunidade é analisada de maneira estruturada e comparável.\n\nPor exemplo, um caso de uso pode ter um potencial de impacto muito alto, mas não ser priorizado no curto prazo caso a empresa ainda não possua dados suficientes ou condições técnicas para executá-lo com segurança e qualidade.\n\nO resultado é um processo de decisão mais transparente, orientado por critérios de negócio e evidências concretas. Na prática, o C-level continua participando das decisões, mas passa a ter uma visão mais clara sobre quais iniciativas possuem maior potencial de retorno e quais dependem de evoluções prévias para gerar valor.",
  },
]

const radarParaQuem = {
  sections: [
    {
      title: 'Para quem vender',
      items: [
        'CEOs e COOs querendo parar de responder ao board sem dados concretos sobre IA.',
        'VPs de Inovação com orçamento disponível, mas sem direcionamento claro — ou com múltiplas iniciativas sem critérios de priorização.',
        'Diretores de TI com pressão para "usar IA", mas sem clareza sobre onde gerar mais impacto para o negócio.',
        'Empresas que já realizaram POCs (Proof of Concepts) que não avançaram para produção.',
        'Empresas com exigências crescentes de compliance (saúde, financeiro, jurídico) que precisam de um framework regulatório para adoção de IA.',
      ],
    },
    {
      title: 'Sinais de que o cliente está pronto para o Radar',
      items: [
        'Board ou liderança cobrando resultados de IA, mas sem clareza sobre onde investir.',
        'Tentativas anteriores de IA que não geraram resultados mensuráveis.',
        'Shadow AI acontecendo (desenvolvedores e áreas utilizando IA sem governança).',
        'Necessidade de conformidade regulatória específica (LGPD, ANS ou normas setoriais).',
        'Empresas mid-enterprise ou large corporate com dados minimamente estruturados.',
      ],
    },
  ],
}

const aidrivenFaq = [
  {
    question:
      'O que é exatamente o AI-Driven Development? É terceirização de desenvolvimento?',
    answer:
      'Não é terceirização — é embedding. Um engenheiro sênior da Nex2AI entra no time do cliente por 4 a 8 semanas e redesenha a forma como o time trabalha, utilizando as ferramentas que o cliente já possui. O objetivo não é substituir os desenvolvedores do cliente, mas transformar a forma como eles operam. Ao final do embedding, o time funciona de forma autônoma. O que permanece é o método, a configuração e as métricas — não uma dependência da Nex2AI.',
  },
  {
    question: 'O que muda concretamente no dia a dia do time?',
    answer:
      '**Cinco coisas mudam:**\n1. Histórias de usuário escritas com critérios de aceite testáveis em formato Gherkin — o Product Owner utiliza IA para cocriar os critérios antes de repassá-los para o desenvolvimento.\n\n2. Testes gerados em paralelo ao desenvolvimento — a IA escreve os testes primeiro, o desenvolvedor valida e depois implementa a funcionalidade.\n\n3. Code review com uma camada de IA pré-triando o Pull Request antes da revisão humana — o revisor passa a focar em decisões de arquitetura e design, e não em lint ou problemas básicos de código.\n\n4. Documentação que surge como subproduto do processo, e não como uma atividade separada.\n\n5. Métricas auditáveis automaticamente, como velocity por sprint, bugs em produção, cobertura de testes, lead time e custo de IA por Pull Request.',
  },
  {
    question: 'Quanto tempo leva para o time perceber diferença?',
    answer:
      'Os primeiros ganhos costumam aparecer na segunda semana do embedding. A diferença de throughput entre sprints normalmente surge entre a terceira e a quarta semana. O pico de produtividade sustentada tende a ocorrer entre 8 e 12 semanas.\n\nO caso documentado da Nex2AI — uma plataforma de inteligência tributária construída em .NET — apresentou um pico de 261 commits em um único mês, mais de 450 testes automatizados criados no período e uma produtividade equivalente a aproximadamente três desenvolvedores sem o uso de IA.',
  },
  {
    question:
      'Como funciona com sistemas legados ou times de low-code, como OutSystems?',
    answer:
      'Os primeiros ganhos costumam aparecer na segunda semana do embedding. A diferença de throughput entre sprints normalmente surge entre a terceira e a quarta semana. O pico de produtividade sustentada tende a ocorrer entre 8 e 12 semanas.\n\nO caso documentado da Nex2AI — uma plataforma de inteligência tributária construída em .NET — apresentou um pico de 261 commits em um único mês, mais de 450 testes automatizados criados no período e uma produtividade equivalente a aproximadamente três desenvolvedores sem o uso de IA.',
  },
  {
    question:
      'Como a Nex2AI garante que a IA não vai comprometer a segurança do código?',
    answer:
      '**Através de guardrails técnicos definidos desde o primeiro dia:**\n- Branch protection obrigatório no CI, impedindo que código seja enviado para a branch principal sem aprovação humana.\n- Secret scanning automatizado, bloqueando commits com credenciais ou dados sensíveis.\n- Cobertura mínima de testes validada automaticamente pelo pipeline.\n\nAlém disso, a IA nunca aprova Pull Requests. Ela realiza uma pré-triagem, mas a decisão final permanece sempre com um revisor humano. Essa abordagem está alinhada ao conceito de Trustworthy AI, amplamente defendido pelo mercado: a responsabilidade permanece com as pessoas, enquanto a IA atua como aceleradora do processo.',
  },
  {
    question: 'O cliente fica dependente da Nex2AI após o piloto?',
    answer:
      'Não. A principal métrica de sucesso do embedding é justamente o time operar de forma autônoma.\nTodo o método fica documentado dentro do ambiente do cliente — incluindo arquivos como CLAUDE.md, skills, casos de uso e configurações de CI. Esse conhecimento passa a ser um ativo da própria organização, versionado em Git e portável para diferentes ferramentas.\n\nCaso exista um retainer após o piloto, ele tem como objetivo apoiar evolução contínua e atualização das práticas, e não criar dependência da Nex2AI.',
  },
]

const aidrivenParaQuem = {
  sections: [
    {
      title: 'Para quem vender',
      items: [
        'CTOs e Tech Leads com times que ainda não estão extraindo todo o potencial da IA no desenvolvimento.',
        'VPs de Engenharia com pressão para aumentar velocidade e produtividade sem ampliar o quadro de desenvolvedores.',
        'Empresas com Shadow AI descontrolado — desenvolvedores utilizando ferramentas de IA com licenças próprias, sem processo, governança ou padrões definidos.',
        'Scale-ups com equipes entre 10 e 200 desenvolvedores em fase de crescimento acelerado.',
      ],
    },
    {
      title: 'Números para usar em conversa',
      items: [
        '**+60%** de velocity por sprint — caso Unifique/Projetiva (jan–abr/2026).',
        '**764** commits em 3 meses com um arquiteto utilizando IA; o mesmo projeto gerou mais de 450 testes automatizados.',
        'Output equivalente a aproximadamente 3x um desenvolvedor sem IA — dado obtido em projeto real conduzido pela Nex2AI.',
        '**-40%** de bugs em produção no mesmo período.',
        '**-25%** de lead time por feature.',
      ],
    },
  ],
}

const govParaQuem = {
  sections: [
    {
      title: 'Para quem vender',
      items: [
        'CISOs e Chief Data Officers preocupados com LGPD e o aumento das exigências regulatórias.',
        'Áreas de risco e compliance em empresas reguladas (saúde, financeiro, jurídico e energia).',
        'CIOs e CTOs que já escalaram iniciativas de IA, mas não possuem rastreabilidade do que está em produção.',
        'Empresas que participam de licitações ou contratos enterprise que exigem conformidade e governança.',
        'Qualquer empresa com mais de 2 ou 3 sistemas de IA sem inventário formal.',
      ],
    },
    {
      title: 'Sinais de que o cliente precisa de Governança agora',
      items: [
        'Possui modelos de IA em produção sem saber exatamente quantos são ou onde estão.',
        'A **LGPD** já é uma preocupação, mas ninguém sabe quais sistemas de IA utilizam dados pessoais.',
        'A área jurídica ou de compliance começou a fazer perguntas sobre IA.',
        'O board já questionou os riscos relacionados à IA em reuniões recentes.',
      ],
    },
  ],
}

const govFaq = [
  {
    question: 'Governança de IA não é só burocracia e freio para inovação?',
    answer:
      'Essa é a percepção equivocada mais comum — e é a mesma que muitas empresas tinham sobre a **ISO 27001** de Segurança da Informação há 15 anos. Hoje, quem não possui ISO 27001 perde contratos. A mesma coisa tende a acontecer com a Governança de IA.\n\nA IBM descreve bem esse cenário: "falhas na governança de IA custam às organizações milhões em multas regulatórias, falhas de modelo e perda da confiança dos stakeholders".\n\nGovernança bem implementada não desacelera a IA — ela acelera a adoção com controle. O que realmente desacelera é não possuir um framework estruturado e precisar interromper iniciativas para corrigir problemas quando o regulador ou auditor aparece.',
  },
  {
    question: 'O que é exatamente a ISO 42001 e por que ela é importante?',
    answer:
      'A ISO 42001 é o primeiro padrão internacional de gestão de Inteligência Artificial. Lançada em dezembro de 2023, ela representa para a IA o que a ISO 27001 representa para Segurança da Informação.\n\n**O padrão define sete pilares de controle:**\n\n1. Estratégia e política;\n2. Inventário e ciclo de vida dos sistemas de IA;\n3. Gestão de riscos e ética;\n4. Privacidade e dados;\n5. Segurança;\n6. Métricas e KPIs;\n7. Estrutura organizacional.\n\nAlém disso, é um padrão auditável. Isso significa que, quando um regulador (ANPD, ANS, Banco Central, etc.) ou um cliente enterprise perguntar "como vocês governam a IA?", a resposta será um processo verificável e documentado — não apenas uma declaração de intenção.',
  },
  {
    question: 'Como funciona o programa na prática?',
    answer:
      'O programa possui duração de 18 semanas, divididas em cinco fases.\n\n**Fase 0 (Semanas 1-2): Diagnóstico**\n- Inventário dos sistemas de IA existentes;\n- Mapa de riscos reais;\n- Baseline de maturidade.\n\n**Fase 1 (Semanas 3-6): Política e Estrutura**\n- Política de uso responsável assinada pela Alta Direção;\n- Constituição do Comitê de IA;\n- Definição do RACI de governança.\n\n**Fase 2 (Semanas 7-10): Inventário Formal e Compliance**\n- Todos os projetos de IA documentados e classificados;\n- Identificação de gaps de LGPD por projeto.\n\n**Fase 3 (Semanas 11-16): Controles Técnicos**\n- Plataforma de monitoramento;\n- Audit trails;\n- Testes automatizados de viés;\n- Dashboard executivo.\n\n**Fase 4 (Semanas 17-18): Transferência e Handover**\n- Treinamento da equipe interna;\n- Declaração de Aplicabilidade ISO 42001;\n- Termo de Aceite pela Alta Direção.',
  },
  {
    question:
      'O que diferencia a Nex2AI da IBM nesse serviço? A IBM também possui Governança de IA.',
    answer:
      'A IBM possui o watsonx.governance, uma excelente plataforma tecnológica, construída para operar dentro do ecossistema IBM. Ela é forte em monitoramento de modelos em produção, auditoria automatizada e conformidade em escala.\n\nO desafio é que a solução pressupõe a utilização do ecossistema watsonx e normalmente envolve investimentos compatíveis com grandes operações enterprise.\n\nA Nex2AI entrega o framework operacional e a plataforma de controle utilizando as ferramentas que o cliente já possui — Azure, Coda, Azure DevOps e outras soluções já presentes no ambiente da empresa.\n\nSem lock-in de plataforma, sem dependência de um ecossistema específico e com um consultor embeddado que entende o contexto real do cliente.\nPara empresas brasileiras de médio e grande porte, essa diferença costuma ser determinante para a viabilidade da implementação.',
  },
  {
    question:
      'Empresas com poucos projetos de IA precisam de Governança agora?',
    answer:
      'Especialmente elas.\nQuando uma empresa possui apenas um ou dois projetos de IA, o custo de implementação da governança é menor e os riscos são mais fáceis de controlar.\n\nQuando existem dezenas de sistemas em produção sem inventário formal, política definida ou rastreabilidade, o custo de correção aumenta significativamente e o risco regulatório já passa a ser real. O melhor momento para iniciar a governança foi quando o primeiro modelo entrou em produção. O segundo melhor momento é agora.\n\nUm dado da Deloitte é revelador: apenas uma em cada cinco empresas possui um modelo maduro de governança para agentes autônomos de IA.\nQuem implementar governança agora tende a construir uma vantagem competitiva relevante à medida que a regulamentação se torna mais rigorosa.',
  },
  {
    question: 'O que fica com o cliente ao final? Não é apenas um relatório?',
    answer:
      'O entregável é uma plataforma operando, não um relatório.\n\n**Ao final do programa, o cliente recebe:**\n\n1. Política de Uso Responsável de IA assinada pela Alta Direção;\n2. Inventário completo dos sistemas de IA com classificação de risco;\n3. Declaração de Aplicabilidade ISO 42001;\n4. Plataforma de governança (Coda ou equivalente) com os sete pilares configurados;\n5. Time interno treinado para operar o framework;\n6. Comitê de IA constituído e funcionando;\n7. Plano de adequação para os projetos que necessitem correção.\n\nEm outras palavras, a empresa passa a operar de forma independente, com a possibilidade de contratar um retainer opcional para evolução e manutenção conforme a regulamentação e os riscos evoluem.',
  },
]

const productAccordionIntro =
  'Agora que você já compreendeu os fundamentos da Nex2AI e a lógica de posicionamento das soluções, vamos explorar com mais profundidade cada serviço, seus contextos de aplicação e a transformação que eles geram para o cliente.'

const products = [
  {
    id: 'radar',
    badge: 'S01',
    title: 'Radar de IA',
    tabs: productTabsConfig,
    contentByTab: {
      pressRelease: {
        eyebrow: 'São Paulo, 2025 — Para publicação imediata',
        headline:
          'Nex2AI lança o Radar de IA, solução que transforma incerteza em um plano executável de adoção de Inteligência Artificial',
        body: 'A Nex2AI anuncia o lançamento do **Radar de IA**, uma solução criada para ajudar empresas a identificar onde investir em inteligência artificial e quais iniciativas possuem maior potencial de impacto para o negócio.\n\nVoltado para organizações que já discutem IA, mas enfrentam dificuldades para priorizar iniciativas, o Radar de IA transforma ideias dispersas em um plano estruturado de evolução **AI-First**.\n\nAo longo de aproximadamente seis semanas, especialistas da Nex2AI avaliam dados, infraestrutura, cultura, processos e governança, identificando oportunidades de maior valor e construindo um roadmap priorizado para adoção de IA.\nA solução utiliza como referência frameworks internacionais, incluindo as normas **ISO 42001** e **ISO 38507**, apoiando empresas na adoção responsável e estruturada da inteligência artificial.\n\n**"O desafio das empresas não é mais entender o potencial da IA, mas decidir onde investir primeiro e como transformar experimentação em resultado real"**, afirma liderança da Nex2AI.\n\nComo resultado, a organização recebe um portfólio priorizado de casos de uso e um plano de execução com recomendações práticas, marcos e próximos passos. Diferentemente de diagnósticos tradicionais, as iniciativas identificadas também podem ser executadas pela própria Nex2AI, conectando estratégia e implementação.\n\nCom o **Radar de IA**, a Nex2AI reforça sua missão de ajudar empresas a acelerar sua transformação AI-First através de estratégia, governança e execução.',
      },
      faq: { faq: radarFaq },
      contratacao: contratacaoContent,
      paraQuem: radarParaQuem,
    },
  },
  {
    id: 'aidriven',
    badge: 'S02',
    title: 'AI-Driven Development',
    tabs: productTabsConfig,
    contentByTab: {
      pressRelease: {
        eyebrow: 'São Paulo, 2025 — Para publicação imediata',
        headline:
          'Nex2AI lança o AI-Driven Development, programa que transforma times de desenvolvimento em equipes potencializadas por IA',
        body: 'Nex2AI lança o **AI-Driven Development**, programa que transforma times de desenvolvimento em equipes potencializadas por IA\nA Nex2AI anuncia o lançamento do AI-Driven Development, um programa criado para ajudar equipes de tecnologia a aumentar produtividade, qualidade e previsibilidade através da adoção estruturada de inteligência artificial no ciclo de desenvolvimento de software.\n\nDiferentemente de iniciativas focadas apenas em ferramentas, o programa combina processos, métricas, arquitetura e práticas operacionais para que os times consigam extrair valor real de plataformas como Claude Code, GitHub Copilot e outras soluções de IA para desenvolvimento.\n\nA abordagem começa com um squad piloto, onde especialistas da Nex2AI trabalham diretamente com a equipe do cliente para redesenhar o fluxo de desenvolvimento, desde a criação das histórias até a entrega em produção. O objetivo é criar um modelo operacional que permita maior velocidade, qualidade e autonomia na utilização de IA.\n\n**"Ter acesso a ferramentas de IA não significa necessariamente gerar resultados. O desafio está em construir processos, práticas e confiança para que os times consigam utilizá-las de forma consistente e escalável"**, afirma a liderança da Nex2AI.\n\nOs resultados observados nas primeiras implementações incluem aumento significativo na capacidade de entrega, redução de bugs em produção, crescimento da cobertura de testes automatizados e maior segurança para evoluções e refatorações de sistemas.\n\nAo final da jornada, o cliente não recebe apenas recomendações, mas um modelo operacional validado na prática, capaz de ser replicado para outros squads e áreas de desenvolvimento.\n\nCom o AI-Driven Development, a Nex2AI amplia seu portfólio de soluções AI-First, ajudando empresas a transformar inteligência artificial em uma capacidade real de engenharia e entrega de software.',
      },
      faq: { faq: aidrivenFaq },
      contratacao: contratacaoContent,
      paraQuem: aidrivenParaQuem,
    },
  },
  {
    id: 'governanca',
    badge: 'S03',
    title: 'Governança em IA',
    tabs: productTabsConfig,
    contentByTab: {
      pressRelease: {
        eyebrow: 'São Paulo, 2025 — Para publicação imediata',
        headline:
          'Nex2AI lança o Programa de Governança de IA para ajudar empresas a adotarem inteligência artificial com segurança, conformidade e rastreabilidade',
        body: 'A Nex2AI anuncia o lançamento do Programa de **Governança de IA**, uma solução criada para ajudar organizações a estruturar políticas, processos e controles capazes de sustentar a adoção de inteligência artificial de forma responsável e alinhada às exigências regulatórias.\n\nÀ medida que o uso de IA se torna cada vez mais comum nas empresas, cresce também a necessidade de entender quais modelos estão em operação, quais riscos estão envolvidos e como garantir conformidade com requisitos internos e externos.\n\nO programa foi desenvolvido para ajudar empresas a sair de um cenário de adoção descentralizada e pouca visibilidade para uma estrutura formal de governança, baseada em boas práticas internacionais e alinhada aos princípios da **ISO 42001**.\n\nDurante a implementação, a Nex2AI apoia a criação de políticas, processos e mecanismos de controle, além de inventariar iniciativas existentes, classificar riscos e estruturar uma rotina contínua de acompanhamento através de um Comitê de IA.\n\n**"O maior risco não está apenas nos modelos que as empresas conhecem, mas também naqueles que já estão sendo utilizados sem visibilidade, governança ou critérios claros de controle"**, afirma a liderança da Nex2AI.\n\nAo final da jornada, a organização passa a contar com um framework auditável de governança, inventário de ativos de IA, classificação de riscos, processos de monitoramento e uma base sólida para evolução em conformidade com normas e exigências regulatórias.\n\nAlém de reduzir riscos operacionais, jurídicos e reputacionais, a governança de IA também se torna um diferencial competitivo, permitindo que empresas estejam mais preparadas para auditorias, contratos enterprise e oportunidades que exigem comprovação de conformidade na utilização de inteligência artificial.',
      },
      faq: { faq: govFaq },
      contratacao: contratacaoContent,
      paraQuem: govParaQuem,
    },
  },
]

const culturePrinciples = [
  {
    title: 'Confiança acima de receita',
    body: 'A Nex2AI só entra em projetos que tem condição de entregar. Se o diagnóstico mostrar que o cliente não está pronto, dados ruins, time resistente, liderança sem patrocínio, a resposta honesta é dizer isso.\n\nIsso parece perda de receita no curto prazo. No médio prazo, é o que gera reputação e recomendação.',
    important:
      'Não force fechamento em cliente que não está pronto. Um piloto menor bem-feito vale mais do que um projeto grande entregue mal.',
  },
  {
    title: 'Começar pequeno, provar, escalar',
    body: 'O modelo da Nex2AI é sempre: **piloto controlado com um time pequeno**, métricas coletadas, case documentado, expansão. Não existe "transformação de toda a empresa em 30 dias". Existe uma squad que muda de patamar em 8 semanas e depois puxa as outras.',
    important:
      'A primeira venda nem sempre é o projeto inteiro. É a porta de entrada. O **Radar de IA** abre para o **AI Squad** ou para a **Governança**. Pense em **LTV**, não em ticket único.',
  },
]

const cultureAiFirst = {
  title: 'O que significa AI-First',
  body: '**"AI-First" descreve uma forma de trabalhar, não uma tecnologia.** Uma empresa AI-First não usa IA como ferramenta adicional ao processo que já tinha, ela redesenha como as coisas são feitas partindo de IA como premissa.\n\nÉ a diferença entre um PO que usa o ChatGPT para escrever histórias mais rápido e um processo de upstream onde a IA co-cria os critérios de aceite, valida ambiguidades antes de chegar no dev, e rastreia o ID da história do card até o PR no repositório.\n\nPara o cliente, AI-First não é conceito. É: mais entregas com o mesmo time, menos retrabalho, decisões baseadas em dados, e uma empresa que o regulador e o cliente final conseguem auditar.\n\nAI-First não é comprar ferramenta. É redesenhar como se trabalha. A Nex2AI faz isso acontecer nos três momentos: antes (estratégia), durante (execução) e depois (governança).',
  calloutLabel: 'Para o cliente, AI-First não é conceito. É:',
  checklist: [
    'Mais entregas com o mesmo time',
    'Menos retrabalho',
    'Decisões baseadas em dados',
    'Uma empresa que o regulador e o cliente final conseguem auditar',
  ],
}

const scrollToSection = id => {
  const element = document.getElementById(id)
  if (element) {
    element.scrollIntoView({ behavior: 'smooth' })
  }
}

export default function BackwardsNex2AI() {
  const [section01Tab, setSection01Tab] = useState('metodologia')
  const [section02Tab, setSection02Tab] = useState('problema')

  // Índice da capa: leva à seção Conhecendo a Nex2AI já na aba certa.
  const goToConhecendo = tab => {
    setSection02Tab(tab)
    scrollToSection('secao-02')
  }

  // O header (barra vermelha + seta) aparece assim que o logo do hero sai da
  // tela. Uma sentinela na posição do logo dispara o IntersectionObserver.
  const [showHeader, setShowHeader] = useState(false)
  const logoSentinelRef = useRef(null)

  useEffect(() => {
    const sentinel = logoSentinelRef.current
    if (!sentinel) {
      return
    }
    const observer = new IntersectionObserver(
      ([entry]) => setShowHeader(!entry.isIntersecting),
      { threshold: 0 },
    )
    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [])

  // Uma única seção "Conhecendo a Nex2AI" com 3 abas (igual ao Figma).
  const conhecendoTabs = [
    { id: 'problema', label: 'O Problema Central' },
    { id: 'produtos', label: 'Produtos' },
    { id: 'cultura', label: 'Cultura AI-First' },
  ]

  // O cabeçalho muda conforme a aba: "O Problema Central" = etapa 02
  // (Conhecendo a Nex2AI); "Produtos"/"Cultura AI-First" = etapa 03 (Nossos
  // Serviços), como no Figma.
  const conhecendoHeader =
    section02Tab === 'problema'
      ? {
          number: '02',
          label: 'Conhecendo a Nex2AI',
          title: 'O que a Nex2AI faz? Por que o cliente deveria se importar?',
          quote:
            '"Antes de qualquer reunião com cliente, você precisa conseguir responder três perguntas em 30 segundos, sem hesitar."',
        }
      : {
          number: '03',
          label: 'Nossos Serviços',
          title: 'Do Diagnóstico à Execução, da Estratégia à Capacitação.',
          quote:
            '"Cada serviço resolve uma etapa diferente da jornada AI-First, mas todos foram projetados para gerar resultado real, não teatro de inovação."',
        }

  const renderConhecendoContent = activeTab => (
    <>
      {activeTab === 'problema' && (
        <>
          <ProblemCards
            intro="Quais problemas que a gente resolve?"
            lead={problemLead}
            cards={problemCards}
            summary={problemSummary}
          />
          <ServicesComparisonTable
            title="Os três produtos e o que cada um resolve"
            lead={comparisonLead}
            columns={comparisonColumns}
            rows={comparisonRows}
          />
          <ValuePropStats
            title="Explicando a proposta de valor em ‘30s’"
            intro={valuePropIntro}
            noteTitle={valuePropNoteTitle}
            note={valuePropNote}
            statsLabel={valuePropStatsLabel}
            stats={valuePropStats}
            closing={valuePropClosing}
          />
        </>
      )}

      {activeTab === 'produtos' && (
        <ProductAccordion
          introHeading="Aprofundando os Serviços Nex2AI"
          intro={productAccordionIntro}
          products={products}
        />
      )}

      {activeTab === 'cultura' && (
        <CultureCallout
          sectionTitle="Princípios que definem como conduzimos oportunidades"
          sectionLead="Esta seção não é sobre técnicas de venda. É sobre os princípios que definem como a Nex2AI conduz oportunidades — e que os nossos parceiros de negócios precisam internalizar para não criar expectativas erradas."
          principles={culturePrinciples}
          aiFirst={cultureAiFirst}
        />
      )}
    </>
  )

  return (
    <div className="min-h-screen w-full bg-white">
      {/* Hero + sentinela na posição do logo (≈92px). Quando ela sai da tela
          por scroll, o header aparece imediatamente. */}
      <div className="relative">
        <span
          ref={logoSentinelRef}
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-0 top-[92px] h-px"
        />
        <BackwardsHero
          eyebrow="Material de Apoio"
          title="Alinhamento de abordagens e apoio"
          titleHighlight="Organizacional."
          subtitle="Transformamos organizações com estratégia, execução e cultura AI-First, do Mapa de Maturidade ao Squad Gerenciado que entrega resultado real."
          badgeTitle="playbook"
          badgeText="Material tático para você dominar o nosso ecossistema de serviços."
        />
      </div>

      {/* Header fixo: desliza para o topo assim que o logo do hero sai da tela */}
      <div
        className={cn(
          'fixed inset-x-0 top-0 z-50 transition-transform duration-300',
          showHeader ? 'translate-y-0' : '-translate-y-full',
        )}
      >
        <PlaybookTopBar
          onBack={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        />
      </div>

      <div className="flex flex-col gap-10 px-20 py-16 lg:flex-row lg:justify-between">
        <div className="flex max-w-[700px] flex-col gap-4">
          <h2
            className="font-titillium-web-bold text-[82px] leading-none tracking-[-0.03em] text-white"
            style={{ WebkitTextStroke: '1px #4D4D4D' }}
          >
            Domine a Narrativa AI-First.
          </h2>
          <p className="font-inter-extrabold text-[40px] leading-[1.08] tracking-[-0.02em] text-[#454545]">
            Posicionamento e Clareza para falar de IA sem ‘hype’.
          </p>
          <p className="font-inter-regular text-base leading-[1.1] tracking-[-0.02em] text-[#454545]">
            Aprenda qual é o posicionamento da Nex2AI e desenvolva clareza
            comercial sobre transformação AI-First, serviços e proposta de
            valor.
          </p>
        </div>

        <div className="w-full lg:max-w-[562px]">
          <SectionIndex
            items={[
              { ...indexItems[0], onClick: () => scrollToSection('secao-01') },
              { ...indexItems[1], onClick: () => goToConhecendo('problema') },
              { ...indexItems[2], onClick: () => goToConhecendo('produtos') },
            ]}
          />
        </div>
      </div>

      <div className="flex flex-col gap-4 bg-gradient-to-r from-[#99020E] to-[#330105] py-12 pl-[139px] pr-20 text-white">
        <h2 className="font-inter-black text-2xl tracking-[-0.033em] text-white">
          Templates, Apresentações e Ferramentas para Acelerar o Comercial.
        </h2>
        <p className="font-inter-regular text-base leading-[1.1] text-white">
          Materiais operacionais desenvolvidos para apoiar reuniões, discovery,
          posicionamento executivo e comunicação dos serviços Nex2AI.
        </p>
      </div>

      <div className="px-20 py-16">
        <h2 className="mb-8 font-inter-black text-2xl tracking-[-0.033em] text-[#454545]">
          Materiais de Apoio Comercial
        </h2>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <SupportMaterialCard
            status="Geral"
            title="Template de Proposta Comercial"
            description="Modelo estruturado de proposta comercial com seções padronizadas para apresentação profissional ao cliente."
            items={[
              'Resumo executivo do desafio',
              'Solução proposta e escopo',
              'Metodologia e timeline',
            ]}
            meta="Google Docs + Word"
            href="https://projectivadt.sharepoint.com/:f:/s/Corporativo/IgAd-Zb3vVClQLv0AP6yTmNpAdloyQJ5y-Z51ALXlP15w8Q?e=dIUnAO"
            target="_blank"
            rel="noopener noreferrer"
          />
          <SupportMaterialCard
            status="Deck para Pitch e outras apresentações"
            title="Modelos de Apresentação"
            description="Acesse apresentações comerciais de apoio a reuniões, discovery, apresentações executivas e contextualização dos serviços da Nex2AI."
            items={[
              'Apresentações institucionais da Nex2AI',
              'Apresentações de Serviços',
              'Templates de discovery e diagnóstico',
            ]}
            meta="PowerPoint + PDF"
            href="https://projectivadt.sharepoint.com/:f:/s/Corporativo/IgAd-Zb3vVClQLv0AP6yTmNpAdloyQJ5y-Z51ALXlP15w8Q?e=dIUnAO"
            target="_blank"
            rel="noopener noreferrer"
          />
        </div>
      </div>

      {/* SEÇÃO 01 */}
      <section id="secao-01" className="relative scroll-mt-20 pt-8">
        <SectionHeader
          number="01"
          label="Como você vai aprender?"
          eyebrow="POR QUE O MÉTODO WORKING BACKWARDS?"
          title="Aprender a explicar pelo impacto do serviço, não pela tecnologia."
          quote="“ Ao invés de começar explicando tecnologia, começamos pelo entendimento do problema, do contexto e da transformação que cada serviço gera.”"
        />

        <div className="px-20 pb-12">
          <SegmentedTabs
            tabs={[
              { id: 'metodologia', label: 'Metodologia' },
              { id: 'pressRelease', label: 'Press Release' },
              { id: 'faq', label: 'FAQ' },
            ]}
            active={section01Tab}
            onChange={setSection01Tab}
          />
        </div>

        {section01Tab === 'metodologia' && (
          <MethodBlock
            intro="Sobre o método"
            body={methodBody}
            bulletsTitle="O que você passa a compreender"
            bullets={methodBullets}
          />
        )}

        {section01Tab === 'pressRelease' && (
          <PressReleaseCard
            heading="O que é um Press Release?"
            intro={pressReleaseIntro}
            eyebrow="Exemplo de Press Release"
            headline="Nex2AI lança o Mapa de Maturidade AI-First, o primeiro diagnóstico de IA que entrega um plano de execução na mesma etapa."
            blocks={pressReleaseBlocks}
          />
        )}

        {section01Tab === 'faq' && (
          <FaqAccordion
            heading="Responda dúvidas e objeções com segurança"
            intro={faqIntro}
            callout={faqCallout}
            eyebrow="Exemplo de Perguntas Frequentes"
            items={faqItems}
          />
        )}

        <SectionFooterNav
          next={{ number: '02', label: 'Conhecendo a Nex2AI' }}
          onNext={() => scrollToSection('secao-02')}
        />
      </section>

      {/* SEÇÃO 02 — Conhecendo a Nex2AI (3 abas, como no Figma) */}
      <section id="secao-02" className="relative scroll-mt-20 pt-8">
        <SectionHeader
          number={conhecendoHeader.number}
          label={conhecendoHeader.label}
          title={conhecendoHeader.title}
          quote={conhecendoHeader.quote}
        />

        <div className="px-20 pb-12">
          <SegmentedTabs
            tabs={conhecendoTabs}
            active={section02Tab}
            onChange={setSection02Tab}
          />
        </div>

        {renderConhecendoContent(section02Tab)}

        <SectionFooterNav
          prev={{ number: '01', label: 'Como você vai aprender' }}
          onPrev={() => scrollToSection('secao-01')}
        />
      </section>

      {/* Widget fixo: acompanha o scroll e flutua sobre o conteúdo no hover */}
      <LearningObjectivesCard objectives={learningObjectives} />
    </div>
  )
}
